import type { GameEvent } from "../core/constants";

export class AudioSystem {
  private context: AudioContext | null = null;
  private muted = false;
  private lastWarningBeat = -1;

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (muted && this.context?.state === "running") void this.context.suspend();
  }

  async unlock(): Promise<void> {
    if (this.muted) return;
    this.context ??= new AudioContext();
    if (this.context.state !== "running") await this.context.resume();
  }

  consume(events: GameEvent[]): void {
    if (this.muted || !this.context || this.context.state !== "running") return;
    for (const event of events) {
      if (event.type === "material") {
        this.tone(event.material === "core" ? 310 : 105, event.blocked ? 0.035 : 0.065, event.material === "core" ? "square" : "sine", 0.035);
      } else if (event.type === "impact") {
        const frequency = event.material === "core" ? 420 + Math.min(380, event.speed) : 70 + Math.min(90, event.speed * 0.12);
        this.tone(frequency, event.material === "core" ? 0.04 : 0.09, event.material === "core" ? "square" : "triangle", 0.025);
      } else if (event.type === "piston") {
        this.sequence([95, 260, 520], 0.025);
      } else if (event.type === "hazardBreak") {
        this.sequence([240, 480, 720], 0.018);
      } else if (event.type === "overdrive") {
        this.sequence([220, 330, 440, 660, 880], 0.035);
      } else if (event.type === "patentCollected") {
        this.sequence(event.added ? [330, 495, 660] : [330, 330], 0.035);
      } else if (event.type === "patentLost") {
        this.sequence([190, 120], 0.07);
      } else if (event.type === "death") {
        this.sequence([170, 120, 72], 0.09);
      }
    }
  }

  sterilizerWarning(distance: number): void {
    const context = this.context;
    if (this.muted || !context || context.state !== "running" || distance > 125) return;
    const beat = Math.floor(context.currentTime * 4);
    if (beat === this.lastWarningBeat) return;
    this.lastWarningBeat = beat;
    this.tone(52, 0.035, "sawtooth", 0.012);
  }

  private tone(frequency: number, duration: number, shape: OscillatorType, volume: number, delay = 0): void {
    const context = this.context;
    if (!context) return;
    const start = context.currentTime + delay;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = shape;
    oscillator.frequency.setValueAtTime(frequency, start);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(volume, start + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.01);
  }

  private sequence(frequencies: number[], spacing: number): void {
    for (let index = 0; index < frequencies.length; index += 1) {
      const frequency = frequencies[index];
      if (frequency !== undefined) this.tone(frequency, spacing * 1.4, "square", 0.025, index * spacing);
    }
  }
}
